import { useEffect, useRef } from 'react';
import { api } from '../api';

// Intervalle entre deux battements de cœur (60 s) et plafond par appel côté API (120 s)
const HEARTBEAT_INTERVAL_MS = 60_000;
const MAX_SECONDS_PER_CALL = 120;

/**
 * Envoie le temps passé sur un cours tant que l'apprenant est sur la page
 * (sprint « traçabilité apprenants » — 01/10/2026).
 * - un envoi toutes les 60 s, uniquement si l'onglet est visible ;
 * - un envoi immédiat au masquage de l'onglet (correctif audit 01/10/2026 : ce flush
 *   était du code mort car `send()` commençait par un test de visibilité) ;
 * - un dernier envoi au démontage de la page ;
 * - aucun envoi sans cours ouvert (courseId requis) ;
 * - le temps masqué n'est jamais compté (le compteur repart à zéro au retour).
 */
export const useCourseTimeTracking = (courseId?: string, enabled: boolean = true) => {
  const lastSentRef = useRef<number | null>(null);

  useEffect(() => {
    if (!courseId || !enabled) return;

    lastSentRef.current = Date.now();

    // Enregistre le temps écoulé depuis le dernier envoi, sans condition de visibilité.
    const flush = () => {
      const now = Date.now();
      const since = lastSentRef.current ? Math.round((now - lastSentRef.current) / 1000) : 0;
      lastSentRef.current = now;
      const seconds = Math.max(0, Math.min(MAX_SECONDS_PER_CALL, since));
      if (seconds <= 0) return;
      api.post(`/Student/Student/courses/${courseId}/heartbeat`, { seconds }).catch((error) => {
        console.warn('Heartbeat temps passé ignoré:', error?.message || error);
      });
    };

    // Battement de cœur régulier, uniquement si l'onglet est visible
    const send = () => {
      if (document.visibilityState !== 'visible') return;
      flush();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Retour sur l'onglet : on repart de maintenant pour ne pas compter le temps masqué
        lastSentRef.current = Date.now();
      } else {
        // Masquage : on enregistre le temps réellement passé avant de perdre le focus
        flush();
      }
    };

    const interval = setInterval(send, HEARTBEAT_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', flush);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', flush);
      // Dernier envoi au démontage de la page
      flush();
    };
  }, [courseId, enabled]);
};

export default useCourseTimeTracking;
